/**
 * Step 9.G — Payer Economics.
 * Each payer sees its own authorised economic layer and below. Upstream economics are filtered
 * server-side: if the API does not return them, they do not exist for this viewer.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Lock, Minus, RefreshCw } from 'lucide-react';
import api from '../lib/api';

const money = (value) =>
  typeof value === 'number'
    ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : '—';

const currentPeriod = () => new Date().toISOString().slice(0, 7);

const TierMove = ({ row }) => {
  if (!row.current_tier && row.current_tier !== 0) return <span className="text-muted-foreground">—</span>;
  const up = row.tier_direction === 'up';
  const down = row.tier_direction === 'down';
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const tone = up ? 'text-emerald-600' : down ? 'text-rose-600' : 'text-muted-foreground';
  return (
    <span className={`inline-flex items-center gap-1 ${tone}`} data-testid="tier-movement">
      <Icon className="h-4 w-4" />
      {row.current_tier}%{row.prior_tier != null && row.prior_tier !== row.current_tier
        ? ` (was ${row.prior_tier}%)` : ''}
    </span>
  );
};

export default function PayerEconomicsPage() {
  const [period, setPeriod] = useState(currentPeriod());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await api.request(`/financials/payer-dashboard?period=${encodeURIComponent(period)}`));
    } catch (err) {
      setData(null);
      setError(err?.message || 'Could not load payer economics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const mine = data?.my_layer;

  return (
    <div className="space-y-8" data-testid="payer-economics-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Payer Economics</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Your economic layer and everyone below it. Nothing above your layer is ever shown.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <label className="text-sm">
            <span className="block text-xs text-muted-foreground mb-1">Residual period</span>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="h-10 rounded-md border bg-background px-3 text-sm"
              data-testid="payer-period-input"
            />
          </label>
          <button
            type="button"
            onClick={load}
            className="h-10 inline-flex items-center gap-2 rounded-full border px-4 text-sm transition-colors hover:bg-muted"
            data-testid="payer-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-600"
             data-testid="payer-error">{error}</div>
      )}

      {data?.blockers?.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid="payer-blockers">
          <p className="flex items-center gap-2 text-sm font-medium text-amber-700">
            <AlertTriangle className="h-4 w-4" />
            This period cannot be approved for payout yet
          </p>
          <ul className="mt-2 space-y-1 text-sm text-amber-700/90">
            {data.blockers.slice(0, 8).map((b, i) => (
              <li key={i}>{b.type.replace(/_/g, ' ')}{b.mid ? ` — MID ${b.mid}` : ''}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ['My eligible residual', mine?.eligible_residual, 'payer-eligible'],
          ['My entitlement', mine?.entitlement, 'payer-entitlement'],
          ['Paid to my downline', data?.downline_payable, 'payer-downline-total'],
          ['My retained payout', data?.retained, 'payer-retained'],
        ].map(([label, value, testid]) => (
          <div key={label} className="rounded-xl border bg-card p-5" data-testid={testid}>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="mt-2 text-2xl font-semibold">{money(value)}</p>
          </div>
        ))}
      </section>

      {data?.native_economics && (
        <section className="rounded-xl border bg-card p-5" data-testid="payer-native-economics">
          <p className="flex items-center gap-2 text-base md:text-lg font-medium">
            <Lock className="h-4 w-4" /> Native processor economics — confidential
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3 text-sm">
            <div><span className="text-muted-foreground">Merchant income</span>
              <p className="text-lg">{money(data.native_economics.merchant_income)}</p></div>
            <div><span className="text-muted-foreground">Processor expense</span>
              <p className="text-lg">{money(data.native_economics.native_processor_expense)}</p></div>
            <div><span className="text-muted-foreground">Native margin</span>
              <p className="text-lg">{money(data.native_economics.native_economic_margin)}</p></div>
          </div>
        </section>
      )}

      <section className="rounded-xl border bg-card" data-testid="payer-downline-table">
        <div className="border-b px-5 py-4">
          <h2 className="text-base md:text-lg font-medium">My line</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {['Party', 'Assigned cost', 'Eligible residual', 'Tier', 'Volume', 'Payout', 'Change']
                  .map((h) => <th key={h} className="px-5 py-3 font-medium">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {[mine, ...(data?.downline || [])].filter(Boolean).map((row) => (
                <tr key={row.party_user_id} className="border-t transition-colors hover:bg-muted/40"
                    data-testid={`payer-row-${row.party_user_id}`}>
                  <td className="px-5 py-3">
                    {row.party_name || row.party_user_id}
                    {row.is_self && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">you</span>}
                  </td>
                  <td className="px-5 py-3">{money(row.assigned_cost)}</td>
                  <td className="px-5 py-3">{money(row.eligible_residual)}</td>
                  <td className="px-5 py-3"><TierMove row={row} /></td>
                  <td className="px-5 py-3">
                    {money(row.current_volume)}
                    {row.volume_change_pct != null && (
                      <span className={`ml-2 text-xs ${row.volume_change_pct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {row.volume_change_pct >= 0 ? '+' : ''}{row.volume_change_pct}%
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 font-medium">{money(row.final_payable)}</td>
                  <td className="px-5 py-3">{money(row.payout_impact)}</td>
                </tr>
              ))}
              {!loading && !mine && !(data?.downline || []).length && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground"
                        data-testid="payer-empty">
                  No calculated economics for {period} yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
